import { useEffect, useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import brandBg from "./assets/brand-bg-clean.png";
import visionBg from "./assets/vision-bg-clean.png";
import contactBg from "./assets/contact-bg-clean.png";
import Logo from "./components/Logo";

const ease = [0.22, 1, 0.36, 1] as const;

type Lang = "zh" | "en";

/* Refined thin-line iconography — SF-Symbols-inspired, 1.25 hairline stroke,
   restrained monochrome. Replaces the heavier lucide glyphs for a calmer,
   more premium (Apple-grade) contact directory. */
type IconProps = { className?: string };

function IconMail({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M3.5 7.5 12 13l8.5-5.5" />
    </svg>
  );
}

function IconPhone({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6.5 3.5h3l1.4 4-2 1.3a12 12 0 0 0 5 5l1.3-2 4 1.4v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" />
    </svg>
  );
}

function IconPin({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 21c4-4.2 6.5-7.7 6.5-11a6.5 6.5 0 1 0-13 0c0 3.3 2.5 6.8 6.5 11Z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  );
}

/** Flowing aurora background, reused on every frame. `tintClass` shifts each frame's mood.
 *  `src` lets a frame swap in its own aurora plate while keeping the identical
 *  processing pipeline (two-layer flow + ink darken + tint) for style parity. */
function AuroraBg({ tint, src = brandBg }: { tint: string; src?: string }) {
  const reduce = useReducedMotion();
  return (
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={src}
        alt=""
        className={`absolute inset-0 h-full w-full origin-center object-cover will-change-transform ${
          reduce ? "" : "animate-aurora-flow"
        }`}
      />
      <img
        src={src}
        alt=""
        aria-hidden
        className={`absolute inset-0 h-full w-full origin-center object-cover mix-blend-screen will-change-transform ${
          reduce ? "opacity-0" : "animate-aurora-flow2 animate-aurora-glow"
        }`}
      />
      <div className="absolute inset-0 bg-ink-950/35" />
      <div className={`absolute inset-0 ${tint}`} />
    </div>
  );
}

/** Shared horizontal safe-margin — every element aligns to this gutter. */
const GUTTER = "px-[clamp(1.5rem,5.2vw,5rem)]";

function Frame({
  id,
  tint,
  bg,
  children,
}: {
  id: string;
  tint: string;
  bg?: string;
  children: ReactNode;
}) {
  return (
    <section
      data-frame={id}
      className={`relative flex h-[100svh] w-full snap-start items-center overflow-hidden pb-24 pt-28 md:pb-28 md:pt-36 ${GUTTER}`}
    >
      <AuroraBg tint={tint} src={bg} />
      <div className="relative z-10 mx-auto w-full max-w-[1440px]">{children}</div>
    </section>
  );
}

const FRAMES = ["brand", "vision", "outlook", "contact"];

/** Restrained bilingual toggle — a single hairline-framed pill with a sliding
 *  active state. Sits in the persistent header, matching the aurora deck's
 *  quiet, editorial tone. */
function LangToggle({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <div className="pointer-events-auto relative flex items-center rounded-full border border-white/15 bg-white/[0.04] p-0.5 backdrop-blur-md">
      <motion.span
        aria-hidden
        layout
        transition={{ type: "spring", stiffness: 380, damping: 34 }}
        className="absolute top-0.5 bottom-0.5 rounded-full bg-white/90"
        style={{
          left: lang === "zh" ? "2px" : "50%",
          right: lang === "zh" ? "50%" : "2px",
        }}
      />
      <button
        onClick={() => onChange("zh")}
        aria-pressed={lang === "zh"}
        className={`relative z-10 w-7 py-1 text-center font-display text-[12px] leading-none tracking-[0.02em] transition-colors duration-300 ${
          lang === "zh" ? "text-ink-950" : "text-white/55 hover:text-white/80"
        }`}
      >
        中
      </button>
      <button
        onClick={() => onChange("en")}
        aria-pressed={lang === "en"}
        className={`relative z-10 w-7 py-1 text-center font-display text-[11px] font-medium uppercase leading-none tracking-[0.06em] transition-colors duration-300 ${
          lang === "en" ? "text-ink-950" : "text-white/55 hover:text-white/80"
        }`}
      >
        EN
      </button>
    </div>
  );
}

function App() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [lang, setLang] = useState<Lang>("zh");
  const rootRef = useRef<HTMLElement | null>(null);

  const zh = lang === "zh";

  // reflect language on <html lang> for a11y / correct font shaping
  useEffect(() => {
    document.documentElement.lang = zh ? "zh-CN" : "en";
  }, [zh]);

  // Track active frame via IntersectionObserver on #root scroll container
  useEffect(() => {
    const root = document.getElementById("root");
    rootRef.current = root as HTMLElement;
    const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-frame]"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = FRAMES.indexOf((e.target as HTMLElement).dataset.frame || "");
            if (idx >= 0) setActive(idx);
          }
        });
      },
      { root, threshold: 0.6 }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const goTo = (i: number) => {
    const target = document.querySelector<HTMLElement>(`[data-frame="${FRAMES[i]}"]`);
    target?.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
  };

  // English uses tighter Latin display type; Chinese keeps the CJK scale.
  const dispFont = zh ? "font-display" : "optical-display font-display";

  return (
    <>
      {/* persistent brand header — logo · tagline · language toggle share one baseline */}
      <div className={`pointer-events-none fixed inset-x-0 top-0 z-30 flex items-center justify-between py-7 md:py-9 ${GUTTER}`}>
        <button onClick={() => goTo(0)} className="pointer-events-auto" aria-label="Buywant">
          <Logo className="text-[1.5rem] md:text-[1.85rem]" />
        </button>
        <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-4 md:gap-5">
          <span className="font-display text-[9px] font-normal uppercase tracking-[0.22em] text-white/55 sm:text-[10px] sm:tracking-[0.28em] md:text-[11px] md:tracking-[0.32em]">
            Model&nbsp;<span className="text-white/30">×</span>&nbsp;Agentic
          </span>
          <span className="hidden h-3 w-px bg-white/15 sm:block sm:h-3.5" />
          <LangToggle lang={lang} onChange={setLang} />
        </div>
      </div>
      {/* dot navigation — editorial pagination, no glow */}
      <nav className="fixed right-[clamp(1.1rem,2.4vw,2rem)] top-1/2 z-30 flex -translate-y-1/2 flex-col items-center gap-3.5">
        {FRAMES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={zh ? `前往第 ${i + 1} 帧` : `Go to frame ${i + 1}`}
            className="group relative flex h-2.5 w-2.5 items-center justify-center"
          >
            <span
              className={`block rounded-full transition-all duration-500 ${
                active === i
                  ? "h-2 w-2 bg-white/90"
                  : "h-1 w-1 bg-white/25 group-hover:bg-white/50"
              }`}
            />
          </button>
        ))}
      </nav>
      {/* ---- FRAME 01 · BRAND ---- */}
      <Frame
        id="brand"
        tint="bg-gradient-to-t from-ink-950/70 via-transparent to-ink-950/40"
      >
        <div className="grid grid-cols-12 gap-y-14 md:gap-y-16 lg:gap-y-20">
          {/* main composition — brand manifesto typography */}
          <motion.h1
            key={`h1-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.35 }}
            transition={{ duration: 1.1, delay: 0.05, ease }}
            className={`${dispFont} col-span-12 text-white text-glow lg:col-span-10`}
          >
            {zh ? (
              <>
                <span className="block text-[clamp(3.4rem,12.5vw,10.5rem)] font-medium leading-[0.92] tracking-[-0.03em]">
                  生产
                </span>
                <span className="mt-1.5 flex flex-wrap items-baseline gap-x-[0.12em] pl-[0.03em] md:mt-2.5">
                  <span className="text-aurora font-normal text-[clamp(2.5rem,8vw,7rem)] leading-[0.96] tracking-[-0.02em]">
                    即
                  </span>
                  <span className="font-light text-[clamp(2.7rem,9vw,8rem)] leading-[0.94] tracking-[-0.025em] text-white/95">
                    全球零售
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="block text-[clamp(2.7rem,8.8vw,7.4rem)] font-medium leading-[0.94] tracking-[-0.035em]">
                  Production
                </span>
                <span className="mt-1.5 flex flex-wrap items-baseline gap-x-[0.22em] md:mt-2.5">
                  <span className="text-aurora font-normal text-[clamp(2rem,6vw,5rem)] leading-[0.98] tracking-[-0.025em]">
                    is
                  </span>
                  <span className="font-light text-[clamp(2.2rem,7vw,6rem)] leading-[0.96] tracking-[-0.03em] text-white/95">
                    Global Retail
                  </span>
                </span>
              </>
            )}
          </motion.h1>

          {/* supporting copy — narrow reading column, related to grid */}
          <motion.div
            key={`sub-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.5 }}
            transition={{ duration: 1, delay: 0.32, ease }}
            className="col-span-12 lg:col-span-5 lg:col-start-8"
          >
            <span className="mb-7 block h-px w-12 bg-white/25" />
            <p className="max-w-[30ch] text-[clamp(0.95rem,1.05vw,1.0625rem)] font-light leading-[1.78] tracking-[0] text-white/72">
              {zh ? (
                <>
                  你只需精心研制并生产商品，<span className="font-normal text-white">Buywant</span> 模型包办其余工作，<span className="whitespace-nowrap">带回<span className="text-aurora font-normal">全球收入</span>。</span>
                </>
              ) : (
                <>
                  You focus on creating great products. <span className="font-normal text-white">Buywant</span> handles everything else—bringing them to the world and bringing <span className="text-aurora font-normal">global retail revenue</span> back to you.
                </>
              )}
            </p>
          </motion.div>
        </div>
      </Frame>
      {/* ---- FRAME 02 · VISION & VALUES ---- */}
      <Frame
        id="vision"
        bg={visionBg}
        tint="bg-gradient-to-br from-ink-950/80 via-ink-950/30 to-blue-brand/10"
      >
        <div className="relative w-full">
          {/* section label */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.6 }}
            transition={{ duration: 0.8, ease }}
            className="mb-10 flex items-center gap-4 md:mb-14"
          >
            <span className="h-px w-8 bg-white/30" />

          </motion.div>

          {/* asymmetric 7 / 5 editorial layout — Vision dominant, Values restrained */}
          <div className="grid grid-cols-12 gap-y-20 md:gap-x-10">
            {/* Vision — cols 1-7, primary */}
            <motion.div
              key={`vision-${lang}`}
              initial={reduce ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.35 }}
              transition={{ duration: 1, delay: 0.06, ease }}
              className="col-span-12 md:col-span-7"
            >
              <div className="mb-8 flex items-baseline gap-3">
                <span className="font-display text-[11px] font-normal uppercase tracking-[0.28em] text-white/45">
                  {zh ? "愿景 · Vision" : "Vision"}
                </span>
              </div>
              <h2 className={`font-display font-normal leading-[1.14] tracking-[-0.018em] text-white break-words ${zh ? "text-[clamp(2.15rem,4.6vw,3.75rem)]" : "text-[clamp(1.65rem,3.8vw,3rem)]"}`}>
                {zh ? (
                  <>
                    全球供需终端
                    <br />
                    <span className="text-aurora text-glow">直接交易握手</span>。
                  </>
                ) : (
                  <>
                    Global supply meets global demand—directly,{" "}
                    <span className="text-aurora text-glow">endpoint to endpoint</span>.
                  </>
                )}
              </h2>
              <p className="mt-8 max-w-[40ch] text-[clamp(0.875rem,0.98vw,0.9375rem)] font-light leading-[1.8] tracking-[0] text-white/62 break-words">
                {zh
                  ? "加速计算时代，AI 模型解决全球商品流动的通路，并持续优化经营指标。"
                  : "In the era of accelerated computing, AI models solve how goods move across the world, continuously optimizing the economics of global commerce."}
              </p>
            </motion.div>

            {/* Values — cols 9-12, offset lower, secondary */}
            <motion.div
              key={`values-${lang}`}
              initial={reduce ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.35 }}
              transition={{ duration: 1, delay: 0.22, ease }}
              className="col-span-12 md:col-span-4 md:col-start-9 md:mt-24 md:border-l md:border-white/[0.08] md:pl-9"
            >
              <div className="mb-8 flex items-baseline gap-3">
                <span className="font-display text-[11px] font-normal uppercase tracking-[0.28em] text-white/45">
                  {zh ? "价值观 · Values" : "Values"}
                </span>
              </div>
              <h2 className="text-balance font-display text-[clamp(1.35rem,2.5vw,2rem)] font-light leading-[1.3] tracking-[-0.012em] text-white/90 break-words">
                {zh ? (
                  <>
                    尊重当地市场秩序，恪守
                    <span className="text-aurora text-glow">全球通行的契约精神</span>。
                  </>
                ) : (
                  <>
                    We respect the integrity of every local market and uphold the
                    <span className="text-aurora text-glow"> principles of trust and contract</span> that make global commerce possible.
                  </>
                )}
              </h2>
              <p className="mt-7 max-w-[36ch] text-[clamp(0.8125rem,0.92vw,0.875rem)] font-light leading-[1.82] tracking-[0] text-white/52 break-words">
                {zh
                  ? "我们的顶级工程师和数学家，会建构一个稳定合规的管道以实现全球经营，用优美最佳的算法建模组合实现可持续的增长。"
                  : "Our world-class engineers and mathematicians turn the complexity of global operations into stable, compliant infrastructure—using elegant combinations of models, algorithms, and optimization to deliver sustainable growth."}
              </p>
            </motion.div>
          </div>
        </div>
      </Frame>
      {/* ---- FRAME 03 · OUTLOOK ---- */}
      <Frame
        id="outlook"
        bg={contactBg}
        tint="bg-gradient-to-t from-ink-950/55 via-ink-950/20 to-violet-brand/10"
      >
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-10">
          {/* left rail — oversized title */}
          <motion.div
            key={`outlook-title-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.4 }}
            transition={{ duration: 1, ease }}
            className="col-span-12 md:col-span-4"
          >
            <h2 className={`font-display font-medium text-white text-glow ${zh ? "text-[clamp(3.4rem,7vw,6rem)] leading-[0.98] tracking-[-0.04em]" : "text-[clamp(2.4rem,4.8vw,3.8rem)] leading-[1.02] tracking-[-0.035em]"}`}>
              {zh ? "展望" : "Outlook"}
            </h2>
          </motion.div>

          {/* right column — editorial long-form, controlled reading width */}
          <motion.div
            key={`outlook-body-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.3 }}
            transition={{ duration: 1, delay: 0.15, ease }}
            className="col-span-12 space-y-6 md:col-span-7 md:col-start-6 md:space-y-7"
          >
            {zh ? (
              <>
                <p className="max-w-[46ch] text-[clamp(0.95rem,1.05vw,1.0625rem)] font-light leading-[1.85] tracking-[0] text-white/75">
                  过去，全球贸易依赖组织能力：市场、渠道、物流、合规、资金等复杂环节被分割在不同体系中，只有大型企业才能承担全球经营成本。
                </p>
                <p className="max-w-[46ch] text-[clamp(0.95rem,1.05vw,1.0625rem)] font-light leading-[1.85] tracking-[0] text-white/75">
                  AI 正在改变这一结构。通过领域模型、预测算法、优化计算与 Agent 执行，全球商业路径第一次可以被理解推理、计算和自动执行。
                </p>
                <p className="max-w-[46ch] text-[clamp(0.95rem,1.05vw,1.0625rem)] font-light leading-[1.85] tracking-[0] text-white/75">
                  企业不再需要先建立全球组织才能销售全球市场；只要拥有<span className="font-normal text-white">优秀产品</span>，即可获得全球商业化能力。
                </p>
                <p className="max-w-[46ch] text-[clamp(1.05rem,1.25vw,1.25rem)] font-normal leading-[1.7] tracking-[-0.01em] text-white/90">
                  技术不再寻找商业场景，<span className="text-aurora">商业化本身正在成为技术</span>。
                </p>
              </>
            ) : (
              <>
                <p className="max-w-[48ch] text-[clamp(0.8125rem,0.95vw,0.9375rem)] font-light leading-[1.75] tracking-[0] text-white/75 break-words">
                  Global trade once depended on organizational capacity. Market access, distribution, logistics, compliance, capital, and other critical functions were fragmented across separate systems, leaving only large enterprises able to absorb the cost and complexity of operating globally.
                </p>
                <p className="max-w-[48ch] text-[clamp(0.8125rem,0.95vw,0.9375rem)] font-light leading-[1.75] tracking-[0] text-white/75 break-words">
                  AI is changing this structure. Through domain models, predictive algorithms, optimization engines, and agentic execution, global commerce can, for the first time, be modeled, reasoned over, optimized, and executed automatically.
                </p>
                <p className="max-w-[48ch] text-[clamp(0.8125rem,0.95vw,0.9375rem)] font-light leading-[1.75] tracking-[0] text-white/75 break-words">
                  Companies no longer need to build a global organization before they can sell globally. With a <span className="font-normal text-white">great product</span>, they can now access the capabilities required to commercialize it worldwide.
                </p>
                <p className="max-w-[48ch] text-[clamp(0.9375rem,1.1vw,1.0625rem)] font-normal leading-[1.65] tracking-[-0.01em] text-white/90 break-words">
                  Technology is no longer searching for use cases.<br /><span className="text-aurora">Commercialization itself is becoming technology</span>.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </Frame>
      {/* ---- FRAME 04 · CONTACT ---- */}
      <Frame
        id="contact"
        tint="bg-gradient-to-t from-ink-950/70 via-transparent to-ink-950/40"
      >
        <div className="grid grid-cols-12 items-end gap-y-16 md:gap-x-10">
          {/* left — brand proposition + CTA */}
          <motion.div
            key={`contact-title-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.6 }}
            transition={{ duration: 1, ease }}
            className="col-span-12 md:col-span-6"
          >
            <h2
              className="font-display text-[clamp(2.35rem,5.2vw,4.2rem)] font-normal leading-[1.1] tracking-[-0.02em] text-white text-glow"
            >
              {zh ? (
                <>
                  与我们一起，
                  <br />
                  重构<span className="text-aurora">全球贸易</span>
                </>
              ) : (
                <>
                  Together, we’re
                  <br />
                  reshaping <span className="text-aurora">global trade</span>
                </>
              )}
            </h2>
          </motion.div>

          {/* right — corporate directory */}
          <motion.div
            key={`contact-dir-${lang}`}
            initial={reduce ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.6 }}
            transition={{ duration: 0.9, delay: 0.3, ease }}
            className="col-span-12 grid gap-x-8 gap-y-10 md:col-span-5 md:col-start-8 md:grid-cols-2"
          >
            <div>
              <p className="mb-3 font-display text-[10px] uppercase tracking-[0.26em] text-white/35">{zh ? "联系人" : "Contact"}</p>
              <p className="font-display text-[17px] font-normal text-white">
                {zh ? <>梁 文 <span className="text-white/40">Will Leung</span></> : <>Will Leung</>}
              </p>
              <p className="mt-1.5 text-[13px] text-white/45">{zh ? "创始人 · Founder" : "Founder"}</p>
            </div>
            <div>
              <p className="mb-3 font-display text-[10px] uppercase tracking-[0.26em] text-white/35">{zh ? "联络" : "Reach"}</p>
              <div className="space-y-2.5">
                <a href="mailto:will@buywant.ai" className="group flex items-center gap-2.5 text-[14px] text-white/65 transition-colors hover:text-white">
                  <IconMail className="text-white/40 transition-colors group-hover:text-white/70" /> will@buywant.ai
                </a>
                <a href="tel:+8613542670833" className="group flex items-center gap-2.5 text-[14px] text-white/65 transition-colors hover:text-white">
                  <IconPhone className="text-white/40 transition-colors group-hover:text-white/70" /> +86 135-4267-0833
                </a>
              </div>
            </div>
            <div>
              <p className="mb-3 flex items-center gap-1.5 font-display text-[10px] uppercase tracking-[0.26em] text-white/35">
                <IconPin className="text-white/40" /> {zh ? "深圳 · HQ" : "Shenzhen · HQ"}
              </p>
              <p className="max-w-[240px] text-[13px] font-light leading-[1.7] text-white/50">
                {zh
                  ? "深圳市南山区 高新南环路金地威新中心 C 塔一层 L117 号"
                  : "L117, 1F, Tower C, Gemdale Weixin Center, Gaoxin South Ring Rd, Nanshan District, Shenzhen"}
              </p>
            </div>
            <div>
              <p className="mb-3 flex items-center gap-1.5 font-display text-[10px] uppercase tracking-[0.26em] text-white/35">
                <IconPin className="text-white/40" /> {zh ? "北京" : "Beijing"}
              </p>
              <p className="max-w-[240px] text-[13px] font-light leading-[1.7] text-white/50">
                {zh
                  ? "北京市东城区隆福寺 6 号院 1 号楼"
                  : "Building 1, Courtyard 6, Longfusi, Dongcheng District, Beijing"}
              </p>
            </div>
          </motion.div>
        </div>
      </Frame>
    </>
  );
}

export default App;
